/**
 * <ScribbleButton>: hand-drawn SVG border button.
 *
 * M2.T10c per IMPLEMENTATION_PLAN §7 M2.T10c + PRD §5.4.
 *
 * A button that renders a slightly-wobbly hand-drawn SVG border around its
 * content. The wobble is achieved by displacing the rect corners with small
 * random-looking offsets baked into the path.
 *
 * Two variants: "primary" and "secondary". Both are flat hand-drawn buttons
 * (item 4 removed the primary's coral fill + offset shadow + tilt); the wobbly
 * SVG border + label are the affordance.
 */

import type { ComponentChildren } from "preact";
import { useRef } from "preact/hooks";
import { useInkWetClick } from "../anim/ink-wet.js";

type ScribbleButtonProps = {
  "onClick"?: () => void;
  "children": ComponentChildren;
  "variant"?: "primary" | "secondary";
  "disabled"?: boolean;
  "type"?: "button" | "submit" | "reset";
  "class"?: string;
  "aria-label"?: string;
  /** Test hook passthrough (audit 2026-06-11, lets bespoke buttons migrate
   * to the real component without losing their selectors). */
  "data-testid"?: string;
};

/**
 * The wobbly SVG border. A closed path that approximates a rounded rectangle
 * with slight per-corner path wobble for the hand-drawn look.
 * viewBox: 0 0 120 44 (matches typical button dimensions).
 */
function ScribbleBorder() {
  return (
    <svg
      class="scribble-border"
      viewBox="0 0 120 44"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d={
          // Hand-drawn wobbly rounded rectangle, corners have slight wobble
          "M 8 2 C 8 2 14 1 30 2 C 50 3 80 1 102 2 C 114 2.5 118 4 119 8 " +
          "C 120 12 119 28 119 36 C 119 40 117 42 112 43 " +
          "C 90 44 50 43 18 43 C 10 43 2 41 2 36 " +
          "C 1 28 2 16 2 8 C 2 4 4 2 8 2 Z"
        }
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ScribbleButton({
  onClick,
  children,
  variant = "primary",
  disabled = false,
  type = "button",
  "class": className,
  "aria-label": ariaLabel,
  "data-testid": dataTestId,
}: ScribbleButtonProps) {
  const ref = useRef<HTMLButtonElement | null>(null);
  // M5.T3: ink-wet click feedback. The hook gates itself on the
  // pointerdown event so disabled buttons (which the browser already
  // suppresses interaction on) get no animation.
  useInkWetClick(ref);
  return (
    <button
      ref={ref}
      type={type}
      class={[
        "scribble-btn",
        `scribble-btn--${variant}`,
        disabled && "scribble-btn--disabled",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      disabled={disabled}
      onClick={onClick}
      aria-label={ariaLabel}
      data-testid={dataTestId}
    >
      <ScribbleBorder />
      <span class="scribble-btn-content">{children}</span>
    </button>
  );
}
